// @flow
import * as React from 'react';
import { URL } from './regex';
import { isValidNumber, formatNumber } from 'libphonenumber-js';
import isDate from 'date-fns/is_date';
import format from 'date-fns/format';
// eslint-disable-next-line import/named
import { type IntlShape, FormattedMessage } from 'react-intl';

// Constants
import { SPECIAL_MAKE_LABELS, VEHICLE_VALUES } from 'constants/vehicle';
const OTHER = VEHICLE_VALUES.OTHER;

export const isUrl = (str: string) => URL.test(str);

export const isEmpty = (str?: string) => {
  return (
    (typeof str === 'string' && !str.trim()) ||
    typeof str === 'undefined' ||
    str === null
  );
};

export const camelCaseToLowerCaseUnderscore = (str: string) =>
  str
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(' ')
    .join('_');

export const toCapitalizeFirstWord = (str: string) => {
  if (str) {
    return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
  } else {
    return '';
  }
};

export const toCapitalize = (str: string) => {
  if (str) {
    const strToCapitalize = toCapitalizeFirstWord(str.toLowerCase());
    return strToCapitalize
      .split(' ')
      .reduce((a, b) => `${a} ${toCapitalizeFirstWord(b)}`);
  } else {
    return '';
  }
};

export const toISODate = (date: Date) => {
  if (isDate(date)) {
    return format(date, 'YYYY-MM-DD');
  }
  return null;
};

export const toISOTime = (date: Date) => {
  if (isDate(date)) {
    return format(date, 'HH:mm:ss');
  }
  return null;
};

export const isPhoneNumberValid = (
  phoneNumber: string,
  country: string
): boolean => {
  if (phoneNumber) {
    return isValidNumber(phoneNumber, country);
  }
  return false;
};

export const textToHTML = (text: string) => {
  return text.replace(/(?:\r\n|\r|\n)/g, '<br>');
};

export const formatPhoneNumber = (
  phoneNumber: ?string | null,
  country: string
) => {
  if (phoneNumber && isValidNumber(phoneNumber, country)) {
    return formatNumber(phoneNumber, country, 'National');
  }
  return phoneNumber || '';
};

export const rawPhoneNumber = (phoneNumber: string | null) => {
  if (phoneNumber === null || typeof phoneNumber === 'undefined') {
    return '';
  }
  return phoneNumber.replace(/[^\d]/g, '');
};

export const splitCamelCase = (text: string) => {
  return text.replace(/([a-z](?=[A-Z]))/g, '$1 ');
};

export const specialCharacters = (text: string) => {
  return text.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1');
};

export const truncateText = (text: string, textLimit: number) => {
  const tagRegEx = new RegExp('<\\/?[^>]+(>|$)', 'g');
  const brRegEx = new RegExp('<br\\s*[/]?>', 'gi');
  const textObject = {
    originalText: text,
    formattedText: text,
    cleanText: text.replace(tagRegEx, ''),
    isTruncated: false
  };
  let textLimitAdjustment = textLimit;
  let htmlString;
  let cleanString = '';
  let countHTMLCharacters = 0;
  let remainingText;
  let matches;

  if (textObject['cleanText'].length > textLimit) {
    while (cleanString.length !== textLimit) {
      htmlString = text.substr(0, textLimit + countHTMLCharacters);
      cleanString = htmlString.replace(tagRegEx, '');

      if (cleanString.length === textLimit) {
        textLimitAdjustment = textLimit + countHTMLCharacters;
      }
      countHTMLCharacters = htmlString.length - cleanString.length;
    }

    textObject['formattedText'] =
      text.substr(0, textLimitAdjustment).trim() + '... ';
    textObject['isTruncated'] = true;
    remainingText = text.substr(textLimitAdjustment);
    matches = remainingText && remainingText.match(tagRegEx);

    if (matches) {
      remainingText = matches.join('').replace(brRegEx, '');
      textObject['formattedText'] += remainingText;
    }
  }

  return textObject;
};

export const getFormattedMessageTag = (id: string, defaultMessage: string) => {
  return (
    <FormattedMessage id={id} defaultMessage={defaultMessage}>
      {msg => msg}
    </FormattedMessage>
  );
};

export const getMMLabel = (
  MMlabel: ?string,
  MMOtherLabel: ?string,
  intl: IntlShape
) => {
  if (MMlabel !== OTHER) {
    return getSpecialMakeLabel(MMlabel || '');
  } else if (MMOtherLabel && MMOtherLabel !== OTHER) {
    return MMOtherLabel;
  } else {
    return intl.formatMessage({
      id: 'c8.common.other',
      defaultMessage: 'OTHER'
    });
  }
};

export const getCTAbackButtonLabel = (
  ctaBackButton: string | null,
  dataLabel: string,
  classicLabel: string,
  intl: IntlShape
) => {
  if (ctaBackButton === 'data') {
    return dataLabel;
  } else {
    if (ctaBackButton === 'classic') {
      return intl.formatMessage({
        id: classicLabel
      });
    } else {
      return intl.formatMessage({
        id: 'c8.cta.header.back',
        defaultMessage: 'Back'
      });
    }
  }
};

export const obfuscateWebkeyParams = (param: string): string => {
  const length = param.length;
  const obfuscated = [];
  if (!length) {
    return '';
  }
  let i = 0;
  for (i; i < length; i++) {
    obfuscated.push(Math.floor(Math.random() * 10));
    obfuscated.push(param[length - i - 1]);
  }
  return obfuscated.join('');
};

export const getSpecialMakeLabel = (label: string) => {
  return SPECIAL_MAKE_LABELS[label] || label;
};



// WEBPACK FOOTER //
// ./src/utils/string.js